import React, { useState, useMemo } from "react";

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function isSameDay(d1, d2) {
  if (!d1 || !d2) return false;
  const a = new Date(d1);
  const b = new Date(d2);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatTime(dateInput) {
  if (!dateInput) return "";
  const d = new Date(dateInput);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function ClassCalendar({
  sessions = [],
  selectedDate,
  onSelectDate,
  onScheduleClick,
  onRescheduleClick,
  onCancelClick,
  isTutor = false,
  loading = false
}) {
  const [currentYear, setCurrentYear] = useState(() => (selectedDate ? new Date(selectedDate).getFullYear() : new Date().getFullYear()));
  const [currentMonth, setCurrentMonth] = useState(() => (selectedDate ? new Date(selectedDate).getMonth() : new Date().getMonth()));

  const activeSelectedDate = useMemo(() => {
    return selectedDate ? new Date(selectedDate) : new Date();
  }, [selectedDate]);

  const today = useMemo(() => new Date(), []);

  // Compute 35 or 42 grid dates for the month
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

    const startDayOfWeek = firstDayOfMonth.getDay(); // 0 (Sun) - 6 (Sat)
    const daysInMonth = lastDayOfMonth.getDate();

    const days = [];

    // Previous month filler days
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(currentYear, currentMonth - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(currentYear, currentMonth, i),
        isCurrentMonth: true
      });
    }

    // Next month filler days (to fill 35 or 42 grid cells)
    const totalCells = days.length <= 35 ? 35 : 42;
    const remaining = totalCells - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(currentYear, currentMonth + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  }, [currentYear, currentMonth]);

  // Map sessions by date string YYYY-MM-DD
  const sessionsByDateStr = useMemo(() => {
    const map = new Map();
    sessions.forEach((s) => {
      if (!s.startTime) return;
      const d = new Date(s.startTime);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return map;
  }, [sessions]);

  // Sessions for the currently selected date
  const selectedDaySessions = useMemo(() => {
    const key = `${activeSelectedDate.getFullYear()}-${activeSelectedDate.getMonth()}-${activeSelectedDate.getDate()}`;
    return sessionsByDateStr.get(key) || [];
  }, [activeSelectedDate, sessionsByDateStr]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const handleTodayJump = () => {
    const now = new Date();
    setCurrentYear(now.getFullYear());
    setCurrentMonth(now.getMonth());
    if (typeof onSelectDate === "function") {
      onSelectDate(now);
    }
  };

  return (
    <div
      className="class-calendar-container"
      style={{
        backgroundColor: "#ffffff",
        border: "1.5px solid #e2e8f0",
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
      }}
    >
      {/* ── Calendar Navigation Header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: "1.5px solid #f1f5f9",
          backgroundColor: "#f8fafc",
          flexWrap: "wrap",
          gap: "10px"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>
            {MONTH_NAMES[currentMonth]} {currentYear}
          </h3>
          <button
            type="button"
            onClick={handleTodayJump}
            style={{
              padding: "4px 10px",
              fontSize: "12px",
              fontWeight: "600",
              backgroundColor: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              cursor: "pointer",
              color: "#334155"
            }}
          >
            Today
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isTutor && typeof onScheduleClick === "function" && (
            <button
              type="button"
              className="primary-button"
              onClick={() => onScheduleClick(activeSelectedDate)}
              style={{
                width: "auto",
                padding: "6px 14px",
                fontSize: "13px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <span>+</span> Schedule Class
            </button>
          )}
          <div style={{ display: "flex", gap: "4px" }}>
            <button
              type="button"
              onClick={handlePrevMonth}
              style={{
                background: "#ffffff",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                cursor: "pointer",
                fontWeight: "700",
                color: "#475569"
              }}
              title="Previous Month"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={handleNextMonth}
              style={{
                background: "#ffffff",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                cursor: "pointer",
                fontWeight: "700",
                color: "#475569"
              }}
              title="Next Month"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.8fr) minmax(300px, 1.2fr)" }}>
        {/* ── Left Column: Calendar Grid ── */}
        <div style={{ padding: "16px 20px", borderRight: "1.5px solid #f1f5f9" }}>
          {/* Days of week header */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "8px" }}>
            {DAYS_OF_WEEK.map((d, i) => (
              <div
                key={d}
                style={{
                  textAlign: "center",
                  fontSize: "12px",
                  fontWeight: "700",
                  color: i === 0 || i === 6 ? "#94a3b8" : "#64748b",
                  padding: "4px 0"
                }}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Date Cells Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "6px" }}>
            {calendarDays.map((cell, idx) => {
              const cellDate = cell.date;
              const dateKey = `${cellDate.getFullYear()}-${cellDate.getMonth()}-${cellDate.getDate()}`;
              const daySessions = sessionsByDateStr.get(dateKey) || [];
              const isSelected = isSameDay(cellDate, activeSelectedDate);
              const isCellToday = isSameDay(cellDate, today);

              const hasGroup = daySessions.some((s) => s.sessionType === "group");
              const hasOneOnOne = daySessions.some((s) => s.sessionType === "one_on_one");

              return (
                <div
                  key={idx}
                  onClick={() => {
                    if (typeof onSelectDate === "function") {
                      onSelectDate(cellDate);
                    }
                  }}
                  style={{
                    minHeight: "68px",
                    padding: "6px 8px",
                    borderRadius: "8px",
                    backgroundColor: isSelected
                      ? "#eff6ff"
                      : isCellToday
                      ? "#f8fafc"
                      : cell.isCurrentMonth
                      ? "#ffffff"
                      : "#fafafa",
                    border: isSelected
                      ? "2px solid #2563eb"
                      : isCellToday
                      ? "1.5px solid #93c5fd"
                      : "1px solid #f1f5f9",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    opacity: cell.isCurrentMonth ? 1 : 0.45
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: isSelected || isCellToday ? "700" : "500",
                        color: isSelected
                          ? "#1d4ed8"
                          : isCellToday
                          ? "#2563eb"
                          : cell.isCurrentMonth
                          ? "#1e293b"
                          : "#94a3b8",
                        width: "22px",
                        height: "22px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isCellToday && !isSelected ? "#dbeafe" : "transparent"
                      }}
                    >
                      {cellDate.getDate()}
                    </span>
                    {daySessions.length > 0 && (
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: "700",
                          color: "#475569",
                          backgroundColor: "#f1f5f9",
                          padding: "1px 5px",
                          borderRadius: "4px"
                        }}
                      >
                        {daySessions.length}
                      </span>
                    )}
                  </div>

                  {/* Micro Badge Indicators for Session Types */}
                  {daySessions.length > 0 && (
                    <div style={{ display: "flex", gap: "3px", flexWrap: "wrap", marginTop: "4px" }}>
                      {hasOneOnOne && (
                        <span
                          title="1:1 Private Session"
                          style={{
                            fontSize: "10px",
                            backgroundColor: "#f3e8ff",
                            color: "#7e22ce",
                            border: "1px solid #d8b4fe",
                            borderRadius: "4px",
                            padding: "1px 4px",
                            fontWeight: "600",
                            lineHeight: 1.2
                          }}
                        >
                          1:1
                        </span>
                      )}
                      {hasGroup && (
                        <span
                          title="Group Class Session"
                          style={{
                            fontSize: "10px",
                            backgroundColor: "#ecfdf5",
                            color: "#047857",
                            border: "1px solid #a7f3d0",
                            borderRadius: "4px",
                            padding: "1px 4px",
                            fontWeight: "600",
                            lineHeight: 1.2
                          }}
                        >
                          Group
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginTop: "14px",
              paddingTop: "12px",
              borderTop: "1px solid #f1f5f9",
              fontSize: "12px",
              color: "#64748b"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ backgroundColor: "#f3e8ff", border: "1px solid #d8b4fe", borderRadius: "4px", padding: "1px 5px", fontSize: "10px", fontWeight: "700", color: "#7e22ce" }}>1:1</span>
              <span>Private Session</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ backgroundColor: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: "4px", padding: "1px 5px", fontSize: "10px", fontWeight: "700", color: "#047857" }}>Group</span>
              <span>Group Batch</span>
            </div>
          </div>
        </div>

        {/* ── Right Column: Day Agenda View ── */}
        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", backgroundColor: "#ffffff" }}>
          <div style={{ paddingBottom: "12px", borderBottom: "1.5px solid #f1f5f9", marginBottom: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>
                Selected Day Agenda
              </span>
              <span style={{ fontSize: "11px", fontWeight: "700", color: "#475569", backgroundColor: "#f1f5f9", padding: "1px 8px", borderRadius: "10px" }}>
                {selectedDaySessions.length} {selectedDaySessions.length === 1 ? "slot" : "slots"}
              </span>
            </div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", marginTop: "2px" }}>
              {activeSelectedDate.toLocaleDateString("en-IN", {
                weekday: "long",
                month: "short",
                day: "numeric",
                year: "numeric"
              })}
            </div>
          </div>

          {/* Sessions List */}
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "10px", maxHeight: "420px" }}>
            {selectedDaySessions.length > 0 ? (
              selectedDaySessions.map((session) => {
                const isOneOnOne = session.sessionType === "one_on_one";
                return (
                  <div
                    key={session._id}
                    style={{
                      border: "1.5px solid #e2e8f0",
                      borderRadius: "10px",
                      padding: "14px 16px",
                      backgroundColor: isOneOnOne ? "#faf5ff" : "#f0fdf4",
                      borderLeft: `4px solid ${isOneOnOne ? "#9333ea" : "#059669"}`,
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", marginBottom: "3px" }}>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "700",
                              padding: "2px 7px",
                              borderRadius: "4px",
                              backgroundColor: isOneOnOne ? "#e9d5ff" : "#bbf7d0",
                              color: isOneOnOne ? "#6b21a8" : "#14532d"
                            }}
                          >
                            {isOneOnOne ? "1:1 Private" : "Group Class"}
                          </span>
                          <span style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>
                            {formatTime(session.startTime)} – {formatTime(session.endTime)} ({session.durationMinutes || 60}m)
                          </span>
                        </div>
                        <h4 style={{ margin: "2px 0 0 0", fontSize: "15px", fontWeight: "700", color: "#0f172a" }}>
                          {session.title}
                        </h4>
                      </div>

                      {/* Tutor Actions */}
                      {isTutor && (
                        <div style={{ display: "flex", gap: "4px" }}>
                          {typeof onRescheduleClick === "function" && (
                            <button
                              type="button"
                              onClick={() => onRescheduleClick(session)}
                              style={{
                                background: "#ffffff",
                                border: "1px solid #cbd5e1",
                                borderRadius: "4px",
                                padding: "3px 7px",
                                fontSize: "11px",
                                fontWeight: "600",
                                color: "#334155",
                                cursor: "pointer"
                              }}
                              title="Reschedule"
                            >
                              Reschedule
                            </button>
                          )}
                          {typeof onCancelClick === "function" && (
                            <button
                              type="button"
                              onClick={() => onCancelClick(session)}
                              style={{
                                background: "#ffffff",
                                border: "1px solid #fecaca",
                                borderRadius: "4px",
                                padding: "3px 7px",
                                fontSize: "11px",
                                fontWeight: "600",
                                color: "#dc2626",
                                cursor: "pointer"
                              }}
                              title="Cancel Session"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {session.description && (
                      <p style={{ margin: 0, fontSize: "13px", color: "#475569", lineHeight: "1.4" }}>
                        {session.description}
                      </p>
                    )}

                    {/* Meta tags: Classroom / Teacher / Students */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", fontSize: "12px", color: "#64748b" }}>
                      {session.classroomName && (
                        <span style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", padding: "1px 6px", borderRadius: "4px" }}>
                          Batch: {session.classroomName}
                        </span>
                      )}
                      {!isTutor && session.tutor && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", backgroundColor: "#ffffff", border: "1px solid #e2e8f0", padding: "1px 8px 1px 4px", borderRadius: "12px", fontWeight: "600", color: "#1e293b" }}>
                          <span style={{ width: "18px", height: "18px", borderRadius: "50%", backgroundColor: "#3b82f6", color: "#ffffff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "700" }}>
                            {(session.tutor.name || session.tutor.username || "T")[0].toUpperCase()}
                          </span>
                          <span>{session.tutor.name || session.tutor.username}</span>
                        </span>
                      )}
                      {isTutor && Array.isArray(session.students) && session.students.length > 0 && (
                        <span style={{ fontSize: "12px", color: "#64748b" }}>
                          Students: {session.students.map((st) => st.name || st.username || st.email).join(", ")}
                        </span>
                      )}
                      {session.venue && (
                        <span style={{ color: "#475569" }}>
                          Location: {session.venue}
                        </span>
                      )}
                    </div>

                    {/* Meeting Link CTA */}
                    {session.meetingUrl && (
                      <div style={{ marginTop: "4px" }}>
                        <a
                          href={session.meetingUrl.startsWith("http") ? session.meetingUrl : `https://${session.meetingUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            backgroundColor: "#2563eb",
                            color: "#ffffff",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "700",
                            textDecoration: "none"
                          }}
                        >
                          Join Online Class
                        </a>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  padding: "36px 16px",
                  textAlign: "center",
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  border: "1.5px dashed #cbd5e1"
                }}
              >
                <div style={{ fontWeight: "600", color: "#475569", fontSize: "14px" }}>
                  No sessions scheduled for this day
                </div>
                <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>
                  {isTutor
                    ? "Plan a group batch or 1:1 session for this date"
                    : "You have no upcoming classes on this day"}
                </div>
                {isTutor && typeof onScheduleClick === "function" && (
                  <button
                    type="button"
                    onClick={() => onScheduleClick(activeSelectedDate)}
                    style={{
                      marginTop: "12px",
                      padding: "6px 14px",
                      fontSize: "12px",
                      fontWeight: "600",
                      backgroundColor: "#ffffff",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      cursor: "pointer",
                      color: "#2563eb"
                    }}
                  >
                    + Schedule Session for this Day
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

